import { HouseMarkerInterface } from "@/types/LotMapInterface";

export interface DetectedLotMapSvgData {
  path: string;
  statusPath: string;
  houseMarker: HouseMarkerInterface | null;
}

export interface LotMapHouseMarkerForm {
  houseX: string | number;
  houseY: string | number;
  houseWidth: string | number;
  houseHeight: string | number;
}

export interface LotMapSvgForm extends LotMapHouseMarkerForm {
  path: string;
  statusPath: string;
}

interface SvgPathInfo {
  d: string;
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  area: number;
}

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

export function detectLotMapSvgData(svgSource: string): DetectedLotMapSvgData {
  const parser = new DOMParser();
  const svgDocument = parser.parseFromString(svgSource, "image/svg+xml");

  if (svgDocument.getElementsByTagName("parsererror").length) {
    throw new Error("The SVG content could not be parsed.");
  }

  const paths = Array.from(svgDocument.getElementsByTagName("path"));

  if (paths.length < 2) {
    throw new Error(
      "The SVG must include at least a lot path and a status path."
    );
  }

  const pathInfos = measurePaths(paths);
  const measurablePaths = pathInfos.filter((path) => path.area > 0);

  if (measurablePaths.length < 2) {
    throw new Error("The SVG paths could not be measured.");
  }

  const mainPath = [...measurablePaths].sort((a, b) => b.area - a.area)[0];
  const remainingPaths = measurablePaths.filter(
    (path) => path.index !== mainPath.index
  );
  const housePaths = remainingPaths.filter(isHouseMarkerCandidate);
  const statusPath =
    [...remainingPaths]
      .filter(
        (path) =>
          !housePaths.some((housePath) => housePath.index === path.index)
      )
      .sort((a, b) => b.area - a.area)[0] ??
    [...remainingPaths].sort((a, b) => b.area - a.area)[0];

  return {
    path: mainPath.d,
    statusPath: statusPath.d,
    houseMarker: getHouseMarkerBounds(housePaths),
  };
}

export function isSvgFile(file: File): boolean {
  return (
    file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg")
  );
}

export function applyDetectedLotMapSvgData(
  form: LotMapSvgForm,
  svgSource: string
): void {
  const detectedData = detectLotMapSvgData(svgSource);

  form.path = detectedData.path;
  form.statusPath = detectedData.statusPath;
  setHouseMarkerForm(form, detectedData.houseMarker);
}

export function getHouseMarkerFromForm(
  form: LotMapHouseMarkerForm
): HouseMarkerInterface | null {
  const x = getOptionalNumber(form.houseX);
  const y = getOptionalNumber(form.houseY);
  const width = getOptionalNumber(form.houseWidth);
  const height = getOptionalNumber(form.houseHeight);

  if (x === null || y === null || width === null || height === null) {
    return null;
  }

  return { x, y, width, height };
}

export function clearHouseMarkerForm(form: LotMapHouseMarkerForm): void {
  form.houseX = "";
  form.houseY = "";
  form.houseWidth = "";
  form.houseHeight = "";
}

function measurePaths(paths: SVGPathElement[]): SvgPathInfo[] {
  const measuringSvg = document.createElementNS(SVG_NAMESPACE, "svg");

  measuringSvg.setAttribute("width", "0");
  measuringSvg.setAttribute("height", "0");
  measuringSvg.style.position = "absolute";
  measuringSvg.style.visibility = "hidden";
  measuringSvg.style.pointerEvents = "none";

  document.body.appendChild(measuringSvg);

  try {
    return paths.map((path, index) => {
      const measuringPath = document.createElementNS(SVG_NAMESPACE, "path");
      const d = path.getAttribute("d") || "";

      measuringPath.setAttribute("d", d);
      measuringSvg.appendChild(measuringPath);

      const box = measuringPath.getBBox();

      measuringPath.remove();

      return {
        d,
        index,
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
        area: box.width * box.height,
      };
    });
  } finally {
    measuringSvg.remove();
  }
}

function isHouseMarkerCandidate(path: SvgPathInfo): boolean {
  const minSize = 6;
  const maxSize = 60;
  const aspectRatio = path.width / path.height;

  return (
    path.width >= minSize &&
    path.height >= minSize &&
    path.width <= maxSize &&
    path.height <= maxSize &&
    aspectRatio >= 0.45 &&
    aspectRatio <= 1.8
  );
}

function getHouseMarkerBounds(
  housePaths: SvgPathInfo[]
): HouseMarkerInterface | null {
  if (!housePaths.length) return null;

  const minX = Math.min(...housePaths.map((path) => path.x));
  const minY = Math.min(...housePaths.map((path) => path.y));
  const maxX = Math.max(...housePaths.map((path) => path.x + path.width));
  const maxY = Math.max(...housePaths.map((path) => path.y + path.height));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function setHouseMarkerForm(
  form: LotMapHouseMarkerForm,
  houseMarker: HouseMarkerInterface | null
): void {
  if (!houseMarker) {
    clearHouseMarkerForm(form);
    return;
  }

  form.houseX = houseMarker.x;
  form.houseY = houseMarker.y;
  form.houseWidth = houseMarker.width;
  form.houseHeight = houseMarker.height;
}

function getOptionalNumber(value: string | number): number | null {
  if (value === "") return null;

  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : null;
}
